import React, { useCallback } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { TrackType } from '../../../../api/cc-smarts/models/TrackType';
import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { useContextMenuActions } from '../../../hooks/useContextMenuActions';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';

import type { RefetchAudioButtonQuery } from './__generated__/RefetchAudioButtonQuery.graphql';

export type RefetchAudioButtonProps = {
	buttonText: MessageDescriptor;
	source: 'customizePanel' | 'sourcesPanel';
	shouldRequeue?: boolean;
};

const styles = cssMap({
	buttonWrapper: {
		marginTop: token('space.100'),
	},
});

export const RefetchAudioButton = ({ buttonText, shouldRequeue }: RefetchAudioButtonProps) => {
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const { cloudId, activeContent } = useAudioPlayerStore();
	const environment = useRelayEnvironment();
	const { removeTrack, addTrack } = useContextMenuActions();

	const isLoading = streamingState.state === AudioStreamingState.LOADING;

	const { formatMessage } = useIntl();

	const startNewAudio = useCallback(() => {
		fetchAudio();
	}, [fetchAudio]);

	const stopExistingAudio = useCallback(() => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
	}, [audioRef]);

	const handleRequeueTrack = useCallback(async () => {
		try {
			const playlist = await fetchQuery<RefetchAudioButtonQuery>(
				environment,
				graphql`
					query RefetchAudioButtonQuery($cloudId: ID!) {
						confluence_getPlaylist(cloudId: $cloudId) {
							tracks {
								nodes {
									mainSource
									supportingSources
									type
									mainContent {
										id
									}
								}
							}
						}
					}
				`,
				{ cloudId },
				{ fetchPolicy: 'store-or-network' },
			).toPromise();

			if (playlist?.confluence_getPlaylist?.tracks?.nodes) {
				const activeTrackIsInPlaylist =
					playlist.confluence_getPlaylist.tracks.nodes[0]?.mainContent?.id ===
					activeContent.contentId;
				if (activeTrackIsInPlaylist) {
					const currentTrack = playlist.confluence_getPlaylist.tracks.nodes[0];
					const trackCopy = { ...currentTrack };

					// Temporarily use the second track as the additional resource for ease in testing
					// TODO: Remove this once sources are synced to the audio player store
					const temporaryResource = playlist.confluence_getPlaylist.tracks.nodes[1];

					await removeTrack(
						currentTrack.mainSource,
						[...(currentTrack.supportingSources ?? [])],
						currentTrack.type as TrackType,
					);

					await addTrack(
						trackCopy.mainSource,
						[temporaryResource?.mainSource],
						trackCopy.type as TrackType,
					);
				}
			}
		} catch {
			// Handle error appropriately (maybe a flag with source modification error message?)
		}
	}, [cloudId, environment, removeTrack, addTrack, activeContent]);

	const handleClick = useCallback(async () => {
		stopExistingAudio();
		if (shouldRequeue) {
			await handleRequeueTrack();
			// TODO: Add additional resource(s) to the audio player store so that changes
			// actually take effect upon the next fetch
		}
		startNewAudio();
	}, [startNewAudio, stopExistingAudio, handleRequeueTrack, shouldRequeue]);

	return (
		<Box xcss={styles.buttonWrapper}>
			<Button
				testId="refetch-audio-button"
				shouldFitContainer
				appearance="primary"
				onClick={handleClick}
				isLoading={isLoading}
			>
				{formatMessage(buttonText)}
			</Button>
		</Box>
	);
};
